import React, { useState } from 'react';
import { X, Plus, Users, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

const BulkTokenModal = ({
  isOpen,
  onClose,
  selectedUsers = [],
  onSuccess
}) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('7_days');
  const [customDate, setCustomDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!amount || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (!reason.trim()) {
      setError('Please provide a reason');
      return;
    }

    if (selectedUsers.length === 0) {
      setError('Please select at least one user');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const calculateExpiresAt = () => {
        if (duration === 'custom') return customDate;

        const now = new Date();
        switch (duration) {
          case '7_days': return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();
          case '14_days': return new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000).toISOString();
          case '1_month': return new Date(now.setMonth(now.getMonth() + 1)).toISOString();
          case '3_months': return new Date(now.setMonth(now.getMonth() + 3)).toISOString();
          case '6_months': return new Date(now.setMonth(now.getMonth() + 6)).toISOString();
          case '12_months': return new Date(now.setFullYear(now.getFullYear() + 1)).toISOString();
          default: return null;
        }
      };

      const bulkTokenData = {
        userIds: selectedUsers.map(user => user._id),
        amount: parseInt(amount),
        reason: reason.trim(),
        tokenExpiration: calculateExpiresAt()
      };

      const { bulkGrantTokens } = await import('../../services/adminService');
      const response = await bulkGrantTokens(bulkTokenData);

      if (response) {
        setResults(response);
        onSuccess(response);
      }
    } catch (err) {
      setError(err.message || 'Failed to grant tokens');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAmount('');
    setReason('');
    setError('');
    setResults(null);
    setLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-full bg-green-100">
              <Users className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Bulk Grant Tokens
              </h3>
              <p className="text-sm text-gray-500">
                Grant tokens to {selectedUsers.length} selected user{selectedUsers.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {!results ? (
          <>
            {/* Selected Users */}
            <div className="p-6 border-b border-gray-200 bg-gray-50 max-h-40 overflow-y-auto">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Selected Users ({selectedUsers.length})
              </h4>
              <div className="space-y-2">
                {selectedUsers.map((user, index) => (
                  <div key={user._id} className="flex items-center space-x-3 text-sm">
                    <span className="text-gray-500">#{index + 1}</span>
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">{user.name}</span>
                      <span className="text-gray-500 ml-2">({user.email})</span>
                    </div>
                    <span className="text-gray-600">
                      {user.tokens?.toLocaleString() || 0} tokens
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount to Grant (per user)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="Enter amount"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#075038] focus:border-transparent"
                      required
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <span className="text-gray-500 text-sm">tokens</span>
                    </div>
                  </div>
                </div>

                {/* Duration */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duration
                    </label>
                    <select
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#075038] focus:border-transparent"
                    >
                      <option value="7_days">7 Days</option>
                      <option value="14_days">14 Days</option>
                      <option value="1_month">1 Month</option>
                      <option value="3_months">3 Months</option>
                      <option value="6_months">6 Months</option>
                      <option value="12_months">12 Months</option>
                      <option value="custom">Custom Date</option>
                    </select>
                  </div>

                  {duration === 'custom' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Custom Date
                      </label>
                      <input
                        type="date"
                        value={customDate}
                        min={new Date().toISOString().split('T')[0]}
                        onChange={(e) => setCustomDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#075038] focus:border-transparent"
                        required={duration === 'custom'}
                      />
                    </div>
                  )}
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Enter reason for granting tokens..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#075038] focus:border-transparent resize-none"
                    required
                  />
                </div>

                {/* Preview */}
                {amount && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-900">Preview</span>
                    </div>
                    <div className="text-sm text-blue-800">
                      <p>Total users: {selectedUsers.length}</p>
                      <p>Amount per user: {parseInt(amount || 0).toLocaleString()} tokens</p>
                      <p className="font-semibold">
                        Total tokens to grant: {(parseInt(amount || 0) * selectedUsers.length).toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !amount || !reason.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#075038] rounded-lg hover:bg-[#075038]/90 disabled:bg-[#075038]/50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Granting...</span>
                    </div>
                  ) : (
                    'Grant Tokens'
                  )}
                </button>
              </div>
            </form>
          </>
        ) : (
          /* Results */
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Bulk Token Grant Completed
              </h3>
              <p className="text-gray-600">
                {results.summary?.successful || 0} of {results.summary?.totalUsers || 0} users processed successfully
              </p>
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-gray-900 mb-3">Summary</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Total Users:</span>
                  <span className="ml-2 font-medium">{results.summary?.totalUsers || 0}</span>
                </div>
                <div>
                  <span className="text-gray-600">Successful:</span>
                  <span className="ml-2 font-medium text-green-600">{results.summary?.successful || 0}</span>
                </div>
                <div>
                  <span className="text-gray-600">Failed:</span>
                  <span className="ml-2 font-medium text-red-600">{results.summary?.failed || 0}</span>
                </div>
                <div>
                  <span className="text-gray-600">Total Tokens:</span>
                  <span className="ml-2 font-medium">{results.summary?.totalTokensGranted?.toLocaleString() || 0}</span>
                </div>
              </div>
            </div>

            {/* Results Details */}
            {results.results && results.results.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Results</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {results.results.map((result, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-green-50 rounded text-sm">
                      <div>
                        <span className="font-medium">{result.email}</span>
                        <span className="text-gray-600 ml-2">
                          {result.oldTokens?.toLocaleString()} → {result.newTokens?.toLocaleString()}
                        </span>
                      </div>
                      <span className="text-green-600 font-medium">+{result.amount?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Errors */}
            {results.errors && results.errors.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Errors</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {results.errors.map((error, index) => (
                    <div key={index} className="flex items-center space-x-2 p-2 bg-red-50 rounded text-sm">
                      <XCircle className="w-4 h-4 text-red-600" />
                      <span className="font-medium">{error.userId}</span>
                      <span className="text-red-600">{error.error}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Close Button */}
            <div className="flex justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-white bg-[#075038] rounded-lg hover:bg-[#075038]/90 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkTokenModal;
